import React from 'react';
import {
  RegistrationScreen,
  formatDob,
  type RegistrationConfig,
} from './registration-common.js';

/**
 * SCRN 19 — VISA Registration (CCF-58). Mirrors the Figma `update-reg-visa`
 * design: name / DoB / mother's maiden name / mobile mandatory identity items,
 * then the two VISA-specific checks (credit limit on the Visa credit card,
 * other card holders on the account). Both are TSYS checks the AGENT performs
 * externally, so — faithful to the Figma + CCF-58 — they show only the question
 * plus a "Check TSYS…" instruction and Pass/Fail radios, with NO on-file value
 * (the credit limit / authorised-user name are not held in our data). Then 2FA,
 * mandatory scripting and "Transfer to automated self-service". Reached after a
 * deregistration when the online-card type is VISA (CCF-8/CCF-46).
 *
 * CCF-58 also describes a "Create manual password" option alongside transfer.
 * Issue #729 (@dylan219, 2026-08-31) ruled that the IPW reset option be added
 * to ALL registration screens "in the same way and using the same rules you
 * have on the business and broker screens" — so this screen now carries
 * `ipwReset`, i.e. once registration security passes the agent gets the
 * "Reset internet password" option via the shared #653 registration → SCRN-57
 * seam (the already-contracted, RBAC- + verification-gated
 * `/security/registration/complete-ipw` → `/security/internet-password` path,
 * no new gateway route invented). The underlying gateway reset flow is
 * PTSB-side pending; the option is wired in parallel with their gateway fix
 * and surfaces whatever the gateway returns.
 *
 * CCF-58 also requires the same "not on file" fallbacks the other registration
 * / security screens provide (issues #373 / #374): when the mother's maiden
 * name is not on file, a tick-box reveals the related-products question ("Do
 * you have any other products or accounts with PTSB?"); when the mobile number
 * is not on file, a tick-box reveals the landline. These are enabled by the
 * shared `notOnFileToggles` flag (mirroring Business24 / SCRN-18 — the toggles
 * appear ONLY when the field genuinely has no on-file value, per #348).
 */
const CONFIG: RegistrationConfig = {
  screenCode: 'SCRN-19',
  heading: 'Open24 registration - Visa only',
  // #774 (@dylan219, 2026-09-11): the mobile-number question is always markable
  // Pass/Fail with no alternate question; a mobile FAIL auto-fails 2FA (outcome
  // deferred until all other questions are completed). Supersedes the mobile
  // "not on file" → landline-alternate behaviour of #348/#656/#662/#372.
  mobileQuestionAlwaysMarkable: true,
  mandatory: [
    { key: 'name', label: 'Full name', value: (_q, name) => name },
    { key: 'dob', label: 'Date of birth', value: (q) => formatDob(q.Dob) },
    {
      key: 'maiden',
      label: "Mother's maiden name",
      value: (q) => q.MothersMaidenName,
    },
    {
      key: 'mobile',
      label: 'Mobile number',
      value: (q) => q.MobileNumber ?? q.MaskedMobile,
    },
    // The two VISA-specific checks are verified by the agent in TSYS, not from
    // our data — show the question + a "Check TSYS…" hint and the Pass/Fail
    // radios, never a value (CCF-58 / Figma `update-reg-visa`, issue #48).
    {
      key: 'creditLimit',
      label: 'Credit limit on Visa credit card',
      hint: 'Check TSYS for credit card limit',
      externalCheck: true,
      value: () => undefined,
    },
    {
      key: 'cardholders',
      label: 'Are there any other card holders on the account?',
      hint: 'Check TSYS for authorised user name',
      externalCheck: true,
      value: () => undefined,
    },
  ],
  additional: [],
  requiredAdditional: 0,
  twoFactor: true,
  // CCF-58 acceptance criteria: the maiden-name / mobile-number rows carry a
  // "not on file" tick-box revealing the alternative question (other products /
  // landline), exactly as Business24 (SCRN-18) does. Enables the shared toggle
  // pair without the rest of Open24's `open24Extras` bundle (issues #373/#374).
  notOnFileToggles: true,
  // #729 (@dylan219, 2026-08-31) — once all questions + 2FA pass, offer the
  // Reset Internet Password option with the scoped Back round-trip, in the
  // same way and under the same rules as Business24 / Broker24 (#653). This
  // supersedes the old "manual-password not available in this build" transfer
  // note; the option now exists via the shared registration → SCRN-57 seam.
  ipwReset: true,
};

export function VisaRegistrationScreen(): React.JSX.Element {
  return <RegistrationScreen config={CONFIG} />;
}
